import React from 'react';
import { VFX_EFFECTS, VFX_CHANCE, REMOVED_VFX_IDS } from '../constants/vfx';
import { RANKS } from '../constants/ranks';

export function fmtTokens(n) {
  if (n == null) return '0';
  return Number.isInteger(n) ? n.toLocaleString() : parseFloat(n.toFixed(2)).toLocaleString();
}

export function rollVfx() {
  if (Math.random() < VFX_CHANCE) {
    const ids = VFX_EFFECTS.map(e => e.id);
    return [ids[Math.floor(Math.random() * ids.length)]];
  }
  return null;
}

// CSS class name(s) for a given VFX value (string or array)
export function vfxClass(vfx) {
  if (!vfx) return '';
  const ids = (Array.isArray(vfx) ? vfx : [vfx]).filter(id => !REMOVED_VFX_IDS.includes(id));
  return ids.map(id => `vfx-${id}`).join(' ');
}

// Renders children wrapped in a span with the VFX CSS class(es) applied
export function VfxSpan({ vfx, children, style, className }) {
  const cls = [vfxClass(vfx), className].filter(Boolean).join(' ');
  return <span style={style} className={cls || undefined}>{children}</span>;
}

export function getRank(score) {
  let rank = RANKS[0];
  for (const r of RANKS) {
    if (score >= r.min) rank = r;
  }
  return rank;
}

export function compositeScore(player, actualGameCount) {
  const hardElo = (player.eloImposter + player.eloCrewmate) / 2;
  const count = actualGameCount !== undefined ? actualGameCount : (player.gameCount || 0);
  return count >= 3 ? Math.round(hardElo) : null;
}

// ─── Season Utilities ──────────────────────────────────────────────────────

/**
 * Calculate all-time ELO as the average of each season's ending ELO.
 * Only includes seasons where the player had 3+ games (same threshold as compositeScore).
 * @param {Array} seasonSnapshots - Past season snapshot objects for one player
 * @param {Object} currentPlayer - Live player doc (current season stats)
 * @returns {{ eloImposter: number, eloCrewmate: number }} Averaged ELOs
 */
export function calculateAllTimeElo(seasonSnapshots, currentPlayer) {
  const eligibleSeasons = [];

  // Include past seasons with 3+ games
  for (const snap of seasonSnapshots) {
    if ((snap.gameCount || 0) >= 3) {
      eligibleSeasons.push({
        eloImposter: snap.eloImposter || 1100,
        eloCrewmate: snap.eloCrewmate || 1100,
      });
    }
  }

  // Include current season if 3+ games
  if ((currentPlayer.gameCount || 0) >= 3) {
    eligibleSeasons.push({
      eloImposter: currentPlayer.eloImposter || 1100,
      eloCrewmate: currentPlayer.eloCrewmate || 1100,
    });
  }

  if (eligibleSeasons.length === 0) {
    return { eloImposter: 1100, eloCrewmate: 1100 };
  }

  const avgImposter = eligibleSeasons.reduce((sum, s) => sum + s.eloImposter, 0) / eligibleSeasons.length;
  const avgCrewmate = eligibleSeasons.reduce((sum, s) => sum + s.eloCrewmate, 0) / eligibleSeasons.length;

  return {
    eloImposter: Math.round(avgImposter),
    eloCrewmate: Math.round(avgCrewmate),
  };
}

/**
 * Aggregate stats across all seasons for all-time view.
 * Additive stats are summed; streak stats take the max across all seasons.
 * @param {Array} seasonSnapshots - Past season snapshot objects for one player
 * @param {Object} currentPlayer - Live player doc (current season stats)
 * @returns {Object} Aggregated stats
 */
export function aggregateAllTimeStats(seasonSnapshots, currentPlayer) {
  const additiveFields = [
    'gameCount', 'gamesWon', 'imposterGames', 'imposterWins',
    'crewmateGames', 'crewmateWins', 'totalKills',
    'totalTasksCompleted', 'totalDeathPosition', 'timesKilled'
  ];

  const maxFields = [
    'longestImposterWinStreak', 'longestCrewmateWinStreak', 'longestOverallWinStreak'
  ];

  const result = {};

  // Sum additive fields across all seasons + current
  for (const field of additiveFields) {
    result[field] = (currentPlayer[field] || 0);
    for (const snap of seasonSnapshots) {
      result[field] += (snap[field] || 0);
    }
  }

  // Take max of streak fields across all seasons + current
  for (const field of maxFields) {
    result[field] = Math.max(
      currentPlayer[field] || 0,
      ...seasonSnapshots.map(s => s[field] || 0)
    );
  }

  return result;
}
